/**
 * Event builders/parsers. Every event that touches a relay carries ONLY blinded
 * tags; real ids live inside the encrypted payload (see CLAUDE.md invariants).
 */
import type { EventTemplate, NostrEvent, UnsignedEvent } from 'nostr-tools';
import { finalizeEvent, generateSecretKey, verifyEvent } from 'nostr-tools';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import { blindBoard, blindTarget } from './crypto/blind';
import { boardEncrypt, boardDecrypt, boardCanDecrypt, peerEncrypt, peerDecrypt } from './crypto/nip44';
import { encryptBoardMeta, decryptBoardMeta, type BoardMeta } from './crypto/metadata';
import { wrapEvent, unwrapEvent } from 'nostr-tools/nip59';
import type { PeerCrypto } from './client';
import { KIND_BOARD, KIND_POST, KIND_REACTION, KIND_DELETE, KIND_KEYUPDATE, KIND_GIFTWRAP, KIND_JOIN, KIND_PRESENCE, KIND_RELAYLIST, KIND_RESYNC,
         type PostPayload, type ReactPayload, type DeletePayload, type KeyUpdatePayload, type JoinPayload, type RelayListPayload,
         type ResyncReqPayload, type ResyncKeysPayload, type ReportPayload } from './kinds';
import { KIND_REPORT } from './kinds';

/** In-payload context binding cutover (review flag #2): payloads WITHOUT
 *  board/epoch fields are legacy and accepted only while they are old.
 *  After this timestamp, unbound events are rejected outright — the
 *  transitional weak spot gets a hard end instead of quietly becoming
 *  permanent. ~2026-10-08 (30 days after the binding shipped). */
export const BINDING_REQUIRED_AFTER = 1791417600;

/** In-payload context binding: reject ciphertext carried into the wrong
 *  board/epoch context. Legacy payloads (no fields) pass only if the event
 *  predates BINDING_REQUIRED_AFTER. */
function boundOk(p: { board?: string; epoch?: number }, boardId: string, epoch: number, createdAt: number): boolean {
  if (p.board === undefined && p.epoch === undefined) return createdAt < BINDING_REQUIRED_AFTER;
  if (p.board !== undefined && p.board !== boardId) return false;
  if (p.epoch !== undefined && p.epoch !== epoch) return false;
  return true;
}

export function buildBoardDef(boardId: string, epochKey: string, meta: BoardMeta, isPublic = false): EventTemplate {
  const tags = [['d', boardId], ['alt', 'Encrypted board metadata (relatos)']];
  // Public boards publish the epoch key in the clear so anyone can read/post
  // without an invite. The ciphertext machinery stays identical.
  if (isPublic) tags.push(['t', 'relatos-public'], ['key', epochKey]);
  return {
    kind: KIND_BOARD,
    created_at: Math.floor(Date.now() / 1000),
    tags,
    content: encryptBoardMeta(epochKey, meta),
  };
}

export function readBoardDef(ev: NostrEvent, epochKeys: Record<number, string>): { meta: BoardMeta; epoch: number } | null {
  // try newest epoch first — keys map is small
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    try {
      return { meta: decryptBoardMeta(epochKeys[e], ev.content), epoch: e };
    } catch { /* wrong epoch — try next */ }
  }
  return null;
}

export function buildRelayList(epochKey: string, boardId: string, relays: string[], updatedAt = Math.floor(Date.now() / 1000)): EventTemplate {
  const payload: RelayListPayload = { t: 'relays', relays, updatedAt };
  return {
    kind: KIND_RELAYLIST,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['d', `relatos:${boardId}`], ['alt', 'Encrypted board relay list (relatos)']],
    content: boardEncrypt(epochKey, payload),
  };
}

export function readRelayList(
  ev: NostrEvent,
  epochKeys: Record<number, string>,
  admin?: string, // when given, reject lists not signed by the board admin
): { relays: string[]; updatedAt: number; epoch: number } | null {
  if (admin && ev.pubkey !== admin) return null;
  // try newest epoch first — keys map is small
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    if (!boardCanDecrypt(epochKeys[e], ev.content)) continue;
    try {
      const p = boardDecrypt<RelayListPayload>(epochKeys[e], ev.content);
      if (p.t === 'relays' && Array.isArray(p.relays) && typeof p.updatedAt === 'number') {
        return { relays: p.relays.filter(r => typeof r === 'string'), updatedAt: p.updatedAt, epoch: e };
      }
    } catch { /* try next */ }
  }
  return null;
}

export function buildPost(epochKey: string, boardId: string, epoch: number, payload: PostPayload): EventTemplate {
  const bound: PostPayload = { ...payload, board: boardId, epoch };
  const tags: string[][] = [['h', blindBoard(epochKey, boardId)]];
  if (payload.kind === 'reply' && payload.root) {
    tags.push(['t', blindTarget(epochKey, payload.root)]);
  }
  tags.push(['alt', 'Encrypted forum post (relatos)']);
  return {
    kind: KIND_POST,
    created_at: Math.floor(Date.now() / 1000),
    tags,
    content: boardEncrypt(epochKey, bound),
  };
}

export function readPost(ev: NostrEvent, boardId: string, epochKeys: Record<number, string>): { payload: PostPayload; epoch: number } | null {
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    if (!boardCanDecrypt(epochKeys[e], ev.content)) continue;
    try {
      const p = boardDecrypt<PostPayload>(epochKeys[e], ev.content);
      if (p.t === 'post' && boundOk(p, boardId, e, ev.created_at)) return { payload: p, epoch: e };
    } catch { /* try next */ }
  }
  return null;
}

export function buildReaction(epochKey: string, boardId: string, epoch: number, targetId: string, value = '+'): EventTemplate {
  const payload: ReactPayload = { t: 'react', target: targetId, value, board: boardId, epoch };
  return {
    kind: KIND_REACTION,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['e', blindTarget(epochKey, targetId)], ['h', blindBoard(epochKey, boardId)], ['alt', 'Encrypted reaction (relatos)']],
    content: boardEncrypt(epochKey, payload),
  };
}

export function readReaction(ev: NostrEvent, boardId: string, epochKeys: Record<number, string>): ReactPayload | null {
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    if (!boardCanDecrypt(epochKeys[e], ev.content)) continue;
    try {
      const p = boardDecrypt<ReactPayload>(epochKeys[e], ev.content);
      if (p.t === 'react' && boundOk(p, boardId, e, ev.created_at)) return p;
    } catch { /* try next */ }
  }
  return null;
}

export function buildDelete(epochKey: string, boardId: string, epoch: number, targetId: string): EventTemplate {
  // NIP-09 adapted: kind 5 with blinded tags; the real target id lives in the ciphertext.
  return {
    kind: KIND_DELETE,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['h', blindBoard(epochKey, boardId)], ['e', blindTarget(epochKey, targetId)]],
    content: boardEncrypt(epochKey, { t: 'delete', target: targetId, board: boardId, epoch } satisfies DeletePayload & { t: 'delete' }),
  };
}

export function readDelete(ev: NostrEvent, boardId: string, epochKeys: Record<number, string>): DeletePayload | null {
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    if (!boardCanDecrypt(epochKeys[e], ev.content)) continue;
    try {
      const p = boardDecrypt<DeletePayload>(epochKeys[e], ev.content);
      if (p.t === 'delete' && typeof p.target === 'string' && boundOk(p, boardId, e, ev.created_at)) return p;
    } catch { /* try next */ }
  }
  return null;
}

/**
 * NIP-59 gift-wrapped key distribution (v2 — replaces the old cleartext
 * kind-1114 events whose p-tags leaked the member roster to relays).
 * Returns a fully signed kind-1059 wrap; publish as-is.
 */
export function buildKeyUpdate(adminSk: string, memberPk: string, boardId: string, epoch: number, salt: string): NostrEvent {
  // content is peer-encrypted (NIP-44 admin->member), NOT board-encrypted:
  // the whole point is that only this member can open it. The rumor carries
  // no p tag; the wrap addresses the recipient without revealing the admin.
  const payload: KeyUpdatePayload = { t: 'keyupdate', board: boardId, epoch, salt };
  return wrapEvent(
    { kind: KIND_KEYUPDATE, content: peerEncrypt(adminSk, memberPk, payload), tags: [], created_at: Math.floor(Date.now() / 1000) },
    hexToBytes(adminSk),
    memberPk,
  );
}

/**
 * Unwrap a kind-1059 gift wrap and read the keyupdate rumor inside.
 * Returns the payload + the admin pubkey that sealed it (caller MUST verify
 * it against the board's admin before applying the new epoch).
 */
export function readKeyUpdate(recipientSk: string, ev: NostrEvent): { payload: KeyUpdatePayload; adminPubkey: string } | null {
  try {
    const rumor = unwrapEvent(ev, hexToBytes(recipientSk));
    if (rumor.kind !== KIND_KEYUPDATE) return null;
    const payload = peerDecrypt<KeyUpdatePayload>(recipientSk, rumor.pubkey, rumor.content);
    if (payload.t !== 'keyupdate') return null;
    return { payload, adminPubkey: rumor.pubkey };
  } catch { return null; }
}

// --- async NIP-59 paths for crypto-capability signers (NIP-07 nip44) --------

/** Randomized timestamp, like nip59's — wraps/seals shouldn't leak real time. */
const wrapTime = () => Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 172800);

/**
 * Manual NIP-59 wrap using a PeerCrypto + an event-signing function, so
 * NIP-07 extensions with nip44 support can distribute epoch salts too.
 * rumor (unsigned) -> seal (kind 13, signed by the admin via signEvent)
 * -> wrap (kind 1059, signed by a throwaway ephemeral key).
 */
async function wrapEventAsync(
  rumor: UnsignedEvent,
  crypto: PeerCrypto,
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>,
  recipientPk: string,
): Promise<NostrEvent> {
  const sealedContent = await crypto.encryptTo(recipientPk, JSON.parse(JSON.stringify(rumor)));
  const seal = await signEvent({
    kind: 13,
    created_at: wrapTime(),
    tags: [],
    content: typeof sealedContent === 'string' ? sealedContent : String(sealedContent),
  });
  const ephSk = generateSecretKey();
  return finalizeEvent({
    kind: KIND_GIFTWRAP,
    created_at: wrapTime(),
    tags: [['p', recipientPk]],
    content: peerEncrypt(bytesToHex(ephSk), recipientPk, JSON.parse(JSON.stringify(seal))),
  }, ephSk);
}

/** unwrapEvent for capability signers: two nip44 decrypts via the extension. */
async function unwrapEventAsync(wrap: NostrEvent, crypto: PeerCrypto): Promise<UnsignedEvent | null> {
  try {
    const sealJson = await crypto.decryptFrom(wrap.pubkey, wrap.content);
    const seal = sealJson as NostrEvent;
    if (seal.kind !== 13 || !verifyEvent(seal)) return null;
    const rumorJson = await crypto.decryptFrom(seal.pubkey, seal.content);
    const rumor = rumorJson as UnsignedEvent;
    if (!rumor || rumor.pubkey !== seal.pubkey) return null;
    return rumor;
  } catch { return null; }
}

/** Async buildKeyUpdate for capability signers (see buildKeyUpdate). */
export async function buildKeyUpdateAsync(
  crypto: PeerCrypto,
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>,
  adminPubkey: string,
  memberPk: string,
  boardId: string,
  epoch: number,
  salt: string,
): Promise<NostrEvent> {
  const payload: KeyUpdatePayload = { t: 'keyupdate', board: boardId, epoch, salt };
  const content = await crypto.encryptTo(memberPk, payload);
  return wrapEventAsync(
    { kind: KIND_KEYUPDATE, content, tags: [], created_at: wrapTime(), pubkey: adminPubkey } as UnsignedEvent,
    crypto, signEvent, memberPk,
  );
}

/** Async readKeyUpdate for capability signers (see readKeyUpdate). */
export async function readKeyUpdateAsync(
  crypto: PeerCrypto,
  ev: NostrEvent,
): Promise<{ payload: KeyUpdatePayload; adminPubkey: string } | null> {
  const rumor = await unwrapEventAsync(ev, crypto);
  if (!rumor || rumor.kind !== KIND_KEYUPDATE) return null;
  try {
    const payload = await crypto.decryptFrom(rumor.pubkey, rumor.content) as KeyUpdatePayload;
    if (payload.t !== 'keyupdate') return null;
    return { payload, adminPubkey: rumor.pubkey };
  } catch { return null; }
}

// --- salt resync (member missed a rotation while offline) -------------------

/** Member -> admin: gift-wrapped resync request. Signed by the member's REAL
 *  key — the admin must know who is asking; the wrap hides it from relays. */
export function buildResyncReq(memberSk: string, adminPk: string, boardId: string, haveEpoch: number): NostrEvent {
  const payload: ResyncReqPayload = { t: 'resync-req', board: boardId, haveEpoch };
  return wrapEvent(
    { kind: KIND_RESYNC, content: peerEncrypt(memberSk, adminPk, payload), tags: [], created_at: Math.floor(Date.now() / 1000) },
    hexToBytes(memberSk),
    adminPk,
  );
}

export async function buildResyncReqAsync(
  crypto: PeerCrypto,
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>,
  memberPk: string,
  adminPk: string,
  boardId: string,
  haveEpoch: number,
): Promise<NostrEvent> {
  const payload: ResyncReqPayload = { t: 'resync-req', board: boardId, haveEpoch };
  const content = await crypto.encryptTo(adminPk, payload);
  return wrapEventAsync(
    { kind: KIND_RESYNC, content, tags: [], created_at: wrapTime(), pubkey: memberPk } as UnsignedEvent,
    crypto, signEvent, adminPk,
  );
}

/** Admin -> member: gift-wrapped salt bundle for every epoch above haveEpoch. */
export function buildResyncKeys(adminSk: string, memberPk: string, boardId: string, salts: Record<number, string>): NostrEvent {
  const payload: ResyncKeysPayload = { t: 'resync-keys', board: boardId, salts };
  return wrapEvent(
    { kind: KIND_RESYNC, content: peerEncrypt(adminSk, memberPk, payload), tags: [], created_at: Math.floor(Date.now() / 1000) },
    hexToBytes(adminSk),
    memberPk,
  );
}

export async function buildResyncKeysAsync(
  crypto: PeerCrypto,
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>,
  adminPubkey: string,
  memberPk: string,
  boardId: string,
  salts: Record<number, string>,
): Promise<NostrEvent> {
  const payload: ResyncKeysPayload = { t: 'resync-keys', board: boardId, salts };
  const content = await crypto.encryptTo(memberPk, payload);
  return wrapEventAsync(
    { kind: KIND_RESYNC, content, tags: [], created_at: wrapTime(), pubkey: adminPubkey } as UnsignedEvent,
    crypto, signEvent, memberPk,
  );
}

export type ResyncResult = { req: ResyncReqPayload; from: string } | { keys: ResyncKeysPayload; from: string };

function classifyResync(p: ResyncReqPayload | ResyncKeysPayload, from: string): ResyncResult | null {
  if (p.t === 'resync-req' && typeof (p as ResyncReqPayload).haveEpoch === 'number') return { req: p as ResyncReqPayload, from };
  if (p.t === 'resync-keys' && typeof (p as ResyncKeysPayload).salts === 'object') return { keys: p as ResyncKeysPayload, from };
  return null;
}

/** Read any kind-1116 rumor from a wrap addressed to recipientSk. */
export function readResync(recipientSk: string, ev: NostrEvent): ResyncResult | null {
  try {
    const rumor = unwrapEvent(ev, hexToBytes(recipientSk));
    if (rumor.kind !== KIND_RESYNC) return null;
    const p = peerDecrypt<ResyncReqPayload | ResyncKeysPayload>(recipientSk, rumor.pubkey, rumor.content);
    return classifyResync(p, rumor.pubkey);
  } catch { return null; }
}

/** Async variant for capability signers. */
export async function readResyncAsync(crypto: PeerCrypto, ev: NostrEvent): Promise<ResyncResult | null> {
  const rumor = await unwrapEventAsync(ev, crypto);
  if (!rumor || rumor.kind !== KIND_RESYNC) return null;
  try {
    const p = await crypto.decryptFrom(rumor.pubkey, rumor.content) as ResyncReqPayload | ResyncKeysPayload;
    return classifyResync(p, rumor.pubkey);
  } catch { return null; }
}

// --- reports (NIP-56 semantics, NIP-59 transport) ---------------------------

/** Member -> moderator: gift-wrapped kind-1984 report. Signed by the
 *  reporter's IDENTITY key (moderation needs a stable identity); the wrap
 *  hides reporter AND reported content from relays. */
export function buildReport(reporterSk: string, moderatorPk: string, payload: ReportPayload): NostrEvent {
  return wrapEvent(
    { kind: KIND_REPORT, content: peerEncrypt(reporterSk, moderatorPk, payload), tags: [], created_at: Math.floor(Date.now() / 1000) },
    hexToBytes(reporterSk),
    moderatorPk,
  );
}

/** Capability-signer variant (nip44 extensions). */
export async function buildReportAsync(
  crypto: PeerCrypto,
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>,
  reporterPk: string,
  moderatorPk: string,
  payload: ReportPayload,
): Promise<NostrEvent> {
  const content = await crypto.encryptTo(moderatorPk, payload);
  return wrapEventAsync(
    { kind: KIND_REPORT, content, tags: [], created_at: wrapTime(), pubkey: reporterPk } as UnsignedEvent,
    crypto, signEvent, moderatorPk,
  );
}

/** Moderator side: unwrap a kind-1059 wrap and read a kind-1984 report rumor. */
export function readReport(recipientSk: string, ev: NostrEvent): { report: ReportPayload; from: string } | null {
  try {
    const rumor = unwrapEvent(ev, hexToBytes(recipientSk));
    if (rumor.kind !== KIND_REPORT) return null;
    const p = peerDecrypt<ReportPayload>(recipientSk, rumor.pubkey, rumor.content);
    if (p.t !== 'report' || !p.target) return null;
    return { report: p, from: rumor.pubkey };
  } catch { return null; }
}

export async function readReportAsync(crypto: PeerCrypto, ev: NostrEvent): Promise<{ report: ReportPayload; from: string } | null> {
  const rumor = await unwrapEventAsync(ev, crypto);
  if (!rumor || rumor.kind !== KIND_REPORT) return null;
  try {
    const p = await crypto.decryptFrom(rumor.pubkey, rumor.content) as ReportPayload;
    if (p.t !== 'report' || !p.target) return null;
    return { report: p, from: rumor.pubkey };
  } catch { return null; }
}

export function buildJoin(epochKey: string, boardId: string, epoch: number, name?: string, realPk?: string): EventTemplate {
  return {
    kind: KIND_JOIN,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['h', blindBoard(epochKey, boardId)]],
    content: boardEncrypt(epochKey, { t: 'join', name, board: boardId, epoch, realPk } satisfies JoinPayload & { t: 'join' }),
  };
}

export function readJoin(ev: NostrEvent, boardId: string, epochKeys: Record<number, string>): JoinPayload | null {
  const epochs = Object.keys(epochKeys).map(Number).sort((a, b) => b - a);
  for (const e of epochs) {
    if (!boardCanDecrypt(epochKeys[e], ev.content)) continue;
    try {
      const p = boardDecrypt<JoinPayload>(epochKeys[e], ev.content);
      if (p.t === 'join' && boundOk(p, boardId, e, ev.created_at)) return p;
    } catch { /* try next */ }
  }
  return null;
}

export function buildPresence(epochKey: string, boardId: string): EventTemplate {
  return {
    kind: KIND_PRESENCE,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['h', blindBoard(epochKey, boardId)], ['expiration', String(Math.floor(Date.now() / 1000) + 180)]],
    content: boardEncrypt(epochKey, { t: 'presence', until: Math.floor(Date.now() / 1000) + 180 }),
  };
}
